import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faImage,
  faFileAlt,
  faShieldAlt,
  faBox,
  faDollarSign,
  faClock,
  faMapMarkerAlt,
  faEnvelope,
  faTruck,
  faPen,
  faTrash,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import Card from "../ui/Card";
import MaterialSelect from "./MaterialSelect";
import "../../style/DetailedViewPanel.css";
import UnsavedChangesModal from "../Modals/UnsavedChangesModal";
import { useMaterialTypes } from "../../hooks/useMaterialTypes";
import { uploadListingImage, removeListingImage, fetchListingImages } from "../../api/uploadApi";
import config from "../../config";
import { Snackbar, Alert as MuiAlert } from "@mui/material";

const DetailedViewPanel = forwardRef(
  (
    {
      row,
      darkMode,
      onClose,
      onSave,
      listingsData,
      saveVersion,
      onUnsavedChangesChange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isClosing, setIsClosing] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [images, setImages] = useState([]);
    const [pendingImages, setPendingImages] = useState([]);
    const [imgLoading, setImgLoading] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const photoInputRef = useRef(null);

    const { materialTypes, loading: materialTypesLoading } = useMaterialTypes();

    const [formData, setFormData] = useState({
      material: "",
      item_name: "",
      quantity: "",
      remaining_qty: "",
      cost: "",
      acquisition_cost: "",
      expiry_disposition: "",
      latest_pickup: "",
      delivery_instructions: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      city: "",
      share_contact: false,
      use_default_address: true,
      pickup_time_start: "",
      pickup_time_end: "",
      available_days: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
    });

    const initialFormData = useRef(null);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

    useEffect(() => {
      if (row) {
        const initialData = {
          material: row.material || "",
          item_name: row.item_name || "",
          quantity: row.quantity || "",
          remaining_qty: row.remaining_qty || "",
          cost: row.cost || "",
          acquisition_cost: row.acquisition_cost != null ? row.acquisition_cost : "",
          expiry_disposition: row.expiry_disposition || "",
          latest_pickup: row.latest_pickup
            ? new Date(row.latest_pickup).toISOString().split("T")[0]
            : "",
          delivery_instructions: row.delivery_instructions || "",
          contact_email: row.contact_email || "",
          contact_phone: row.contact_phone || "",
          address: row.address || "",
          city: row.city || "",
          share_contact: row.share_contact || false,
          use_default_address:
            row.use_default_address !== undefined
              ? row.use_default_address
              : true,
          pickup_time_start: row.pickup_time_start || "09:00",
          pickup_time_end: row.pickup_time_end || "17:00",
          available_days: row.available_days || {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
          },
        };
        setFormData(initialData);
        initialFormData.current = JSON.parse(JSON.stringify(initialData));
        setErrors({});
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row?.id]);

    useEffect(() => {
      if (!row?.id) return;
      fetchListingImages(row.id)
        .then((res) => setImages(res.images || []))
        .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row?.id]);

    useEffect(() => {
      if (saveVersion > 0) {
        initialFormData.current = JSON.parse(JSON.stringify(formData));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveVersion]);

    const hasFormChanges = useCallback(() => {
      if (!initialFormData.current) return false;
      if (pendingImages.length > 0) return true;
      return (
        JSON.stringify(formData) !== JSON.stringify(initialFormData.current)
      );
    }, [formData, pendingImages]);

    useEffect(() => {
      if (onUnsavedChangesChange) {
        onUnsavedChangesChange(hasFormChanges());
      }
    }, [formData, onUnsavedChangesChange, hasFormChanges]);

    useImperativeHandle(ref, () => ({
      hasUnsavedChanges: hasFormChanges,
      flushPendingImages: async () => {
        if (!pendingImages.length || !row?.id) return;
        setImgLoading(true);
        try {
          const results = await Promise.allSettled(
            pendingImages.map(({ file }) => uploadListingImage(row.id, file))
          );
          pendingImages.forEach(({ localUrl }) => URL.revokeObjectURL(localUrl));
          setPendingImages([]);
          const succeeded = results.filter(r => r.status === "fulfilled").map(r => r.value);
          const failed = results.filter(r => r.status === "rejected");
          if (succeeded.length) {
            setImages((prev) => [
              ...prev,
              ...succeeded.map((res) => ({ id: res.id, image_url: res.url, original_filename: "" })),
            ]);
          }
          if (failed.length) {
            // try to extract backend message from the error
            let msg = "";
            try { msg = JSON.parse(failed[0].reason?.message)?.message; } catch {}
            setSnackbar({
              open: true,
              severity: "error",
              message: `${failed.length} image${failed.length > 1 ? "s" : ""} failed to upload${msg ? `: ${msg}` : ""}`,
            });
          }
        } finally {
          setImgLoading(false);
        }
      },
      validate: () => {
        const newErrors = {};

        // Validate at least one day is selected
        const hasSelectedDay = Object.values(formData.available_days).some(
          (day) => day
        );
        if (!hasSelectedDay) {
          newErrors.available_days = t(
            "Please select at least one available day"
          );
        }

        // Validate pickup times
        const timeErrors = validatePickupTimes();
        Object.assign(newErrors, timeErrors);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      getUpdatedRow: () => {
        if (!row || !hasFormChanges()) return null;
        return {
          ...row,
          material: formData.material.trim(),
          item_name: formData.item_name.trim() || null,
          quantity: parseFloat(formData.quantity) || 0,
          remaining_qty: parseFloat(formData.remaining_qty) || 0,
          cost: parseFloat(formData.cost) || 0,
          acquisition_cost: formData.acquisition_cost !== "" ? parseFloat(formData.acquisition_cost) : null,
          expiry_disposition: formData.expiry_disposition || null,
          latest_pickup: formData.latest_pickup,
          delivery_instructions: formData.delivery_instructions.trim(),
          contact_email: formData.contact_email.trim(),
          contact_phone: formData.contact_phone.trim(),
          address: formData.address.trim(),
          city: formData.city.trim(),
          share_contact: formData.share_contact,
          use_default_address: formData.use_default_address,
          pickup_time_start: formData.pickup_time_start,
          pickup_time_end: formData.pickup_time_end,
          available_days: formData.available_days,
        };
      },
      resetChanges: () => {
        if (initialFormData.current) {
          setFormData(JSON.parse(JSON.stringify(initialFormData.current)));
        }
      },
      clearPendingImages: () => {
        pendingImages.forEach(({ localUrl }) => URL.revokeObjectURL(localUrl));
        setPendingImages([]);
      },
    }));

    if (!row) return null;

    const handleChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

    const handleClose = () => {
      if (hasFormChanges()) {
        setShowUnsavedModal(true);
        return;
      }
      setIsClosing(true);
      setTimeout(() => onClose(), 300);
    };

    const handleDiscardAndClose = () => {
      pendingImages.forEach(({ localUrl }) => URL.revokeObjectURL(localUrl));
      setPendingImages([]);
      setShowUnsavedModal(false);
      setIsClosing(true);
      setTimeout(() => onClose(), 300);
    };

    const handleCancelClose = () => {
      setShowUnsavedModal(false);
    };

    const handleDayToggle = (day) => {
      setFormData((prev) => ({
        ...prev,
        available_days: {
          ...prev.available_days,
          [day]: !prev.available_days[day],
        },
      }));
      // Clear error when a day is selected
      if (errors.available_days) {
        setErrors((prev) => ({ ...prev, available_days: undefined }));
      }
    };

    const handleCheckboxChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validatePickupTimes = () => {
      const errors = {};
      const startTime = formData.pickup_time_start;
      const endTime = formData.pickup_time_end;
      if (!startTime || !endTime) return errors;

      const startMinutes =
        parseInt(startTime.split(":")[0]) * 60 +
        parseInt(startTime.split(":")[1]);
      const endMinutes =
        parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);

      if (startMinutes >= endMinutes) {
        errors.pickup_time_start = t("Start time must be before end time");
        errors.pickup_time_end = t("End time must be after start time");
      }

      const durationMinutes = endMinutes - startMinutes;
      if (durationMinutes < 60) {
        errors.pickup_time_start = t("Pickup window must be at least 1 hour");
        errors.pickup_time_end = t("Pickup window must be at least 1 hour");
      }

      return errors;
    };

    const handleTimeBlur = () => {
      const timeErrors = validatePickupTimes();
      setErrors((prev) => ({
        ...prev,
        pickup_time_start: timeErrors.pickup_time_start,
        pickup_time_end: timeErrors.pickup_time_end,
      }));
    };

    const daysConfig = [
      { key: "monday", label: "M" },
      { key: "tuesday", label: "T" },
      { key: "wednesday", label: "W" },
      { key: "thursday", label: "Th" },
      { key: "friday", label: "F" },
      { key: "saturday", label: "S" },
      { key: "sunday", label: "Su" },
    ];

    return (
      <>
        <Card
          variant="system"
          darkMode={darkMode}
          className={`detailed-view-panel ${isClosing ? "closing" : ""}`}
        >
          {/* Header */}
          <div className="panel-header">
            <div className="panel-header-left">
              <h3 className="panel-title">
                <span className="panel-title-icon">
                  <FontAwesomeIcon icon={faPen} />
                </span>
                {t("Workspace")}
                <span className="panel-header-separator" />
                <span className="panel-subtitle">
                  {t("Update pricing, quantities, deadlines, and pickup details to keep your listing accurate")}
                </span>
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="close-button"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="panel-content-grid">
            {/* LEFT COLUMN - Material & Quantity Info */}
            <div className="panel-column">
              {/* Material Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon">
                    <FontAwesomeIcon icon={faBox} />
                  </div>
                  <h4 className="info-card-title">{t("Material Details")}</h4>
                </div>
                <div className="info-card-content">
                  <div className="field-row-group">
                    <div className="field-row">
                      <label className="field-label" htmlFor="material">
                        {t("Type")}
                      </label>
                      <MaterialSelect
                        value={formData.material}
                        onChange={(val) => handleChange("material", val)}
                        options={materialTypes}
                        disabled={materialTypesLoading}
                        hasError={!!errors.material}
                      />
                    </div>

                    <div className="field-row">
                      <label className="field-label" htmlFor="item_name">
                        {t("Item Name")}
                      </label>
                      <input
                        id="item_name"
                        type="text"
                        className="field-input"
                        value={formData.item_name}
                        onChange={(e) => handleChange("item_name", e.target.value)}
                        placeholder={t("Optional")}
                        maxLength={200}
                      />
                    </div>
                  </div>

                  <div className="field-row-group">
                    <div className="field-row">
                      <label className="field-label" htmlFor="quantity">
                        {t("Total Qty")}
                      </label>
                      <input
                        id="quantity"
                        type="number"
                        className={`field-input no-spinners ${
                          errors.quantity ? "error" : ""
                        }`}
                        value={formData.quantity}
                        onChange={(e) =>
                          handleChange("quantity", e.target.value)
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      <span className="field-unit">lbs</span>
                    </div>
                    <div className="field-row">
                      <label className="field-label" htmlFor="remaining_qty">
                        {t("Remaining")}
                      </label>
                      <input
                        id="remaining_qty"
                        type="number"
                        className={`field-input no-spinners ${
                          errors.remaining_qty ? "error" : ""
                        }`}
                        value={formData.remaining_qty}
                        onChange={(e) =>
                          handleChange("remaining_qty", e.target.value)
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      <span className="field-unit">lbs</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing & Deadline Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon green">
                    <FontAwesomeIcon icon={faDollarSign} />
                  </div>
                  <h4 className="info-card-title">{t("Pricing & Deadline")}</h4>
                </div>
                <div className="info-card-content">
                  <div className="field-row-group">
                    <div className="field-row">
                      <label className="field-label" htmlFor="cost">
                        {t("Cost")}
                      </label>
                      <div className="field-with-prefix">
                        <span className="field-prefix">$</span>
                        <input
                          id="cost"
                          type="number"
                          className={`field-input no-spinners ${
                            errors.cost ? "error" : ""
                          }`}
                          value={formData.cost}
                          onChange={(e) => handleChange("cost", e.target.value)}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <span className="field-unit">/lb</span>
                    </div>
                    <div className="field-row">
                      <label className="field-label" htmlFor="latest_pickup">
                        {t("Deadline")}
                      </label>
                      <input
                        id="latest_pickup"
                        type="date"
                        className={`field-input ${
                          errors.latest_pickup ? "error" : ""
                        }`}
                        value={formData.latest_pickup}
                        onChange={(e) =>
                          handleChange("latest_pickup", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="field-row-group">
                    <div className="field-row">
                      <label className="field-label" htmlFor="acquisition_cost">
                        {t("Original Cost")}
                      </label>
                      <div className="field-with-prefix">
                        <span className="field-prefix">$</span>
                        <input
                          id="acquisition_cost"
                          type="number"
                          className={`field-input no-spinners`}
                          value={formData.acquisition_cost}
                          onChange={(e) => handleChange("acquisition_cost", e.target.value)}
                          placeholder={t("Optional")}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <span className="field-unit">/lb</span>
                    </div>
                    <div className="field-row">
                      <label className="field-label" htmlFor="expiry_disposition">
                        {t("If Unsold")}
                      </label>
                      <select
                        id="expiry_disposition"
                        className="field-input"
                        value={formData.expiry_disposition}
                        onChange={(e) => handleChange("expiry_disposition", e.target.value)}
                      >
                        <option value="">{t("Not specified")}</option>
                        <option value="landfill">{t("Landfill")}</option>
                        <option value="incineration">{t("Incineration")}</option>
                        <option value="recycling">{t("Recycling")}</option>
                        <option value="relisted">{t("Re-list on Punar")}</option>
                        <option value="unknown">{t("Unknown")}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pickup Schedule Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon blue">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <h4 className="info-card-title">{t("Pickup Schedule")}</h4>
                </div>
                <div className="info-card-content">
                  <div className="field-row-group">
                    <div className="field-row">
                      <label
                        className="field-label"
                        htmlFor="pickup_time_start"
                      >
                        {t("From")}
                      </label>
                      <input
                        id="pickup_time_start"
                        type="time"
                        className={`field-input ${
                          errors.pickup_time_start ? "error" : ""
                        }`}
                        value={formData.pickup_time_start}
                        onChange={(e) =>
                          handleChange("pickup_time_start", e.target.value)
                        }
                        onBlur={handleTimeBlur}
                      />
                    </div>
                    <div className="field-row">
                      <label className="field-label" htmlFor="pickup_time_end">
                        {t("To")}
                      </label>
                      <input
                        id="pickup_time_end"
                        type="time"
                        className={`field-input ${
                          errors.pickup_time_end ? "error" : ""
                        }`}
                        value={formData.pickup_time_end}
                        onChange={(e) =>
                          handleChange("pickup_time_end", e.target.value)
                        }
                        onBlur={handleTimeBlur}
                      />
                    </div>
                  </div>

                  <div className="field-row">
                    <label className="field-label">{t("Days")}</label>
                    <div
                      className={`days-row ${
                        errors.available_days ? "error" : ""
                      }`}
                    >
                      {daysConfig.map((day) => (
                        <button
                          key={day.key}
                          type="button"
                          className={`day-chip ${
                            formData.available_days[day.key] ? "active" : ""
                          }`}
                          onClick={() => handleDayToggle(day.key)}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    {errors.available_days && (
                      <span className="field-error">
                        {errors.available_days}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Instructions Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon teal">
                    <FontAwesomeIcon icon={faTruck} />
                  </div>
                  <h4 className="info-card-title">
                    {t("Delivery Instructions")}
                  </h4>
                </div>
                <div className="info-card-content">
                  <textarea
                    id="delivery_instructions"
                    className="field-textarea"
                    value={formData.delivery_instructions}
                    onChange={(e) =>
                      handleChange("delivery_instructions", e.target.value)
                    }
                    placeholder={t(
                      "Enter any special instructions for pickup or delivery..."
                    )}
                    rows="4"
                  />
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Uploads, Location, Contact */}
            <div className="panel-column">
              {/* Documents Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon amber">
                    <FontAwesomeIcon icon={faFileAlt} />
                  </div>
                  <h4 className="info-card-title">{t("Documents & Media")}</h4>
                </div>
                <div className="info-card-content">
                  {/* saved + pending thumbnails */}
                  {(images.length > 0 || pendingImages.length > 0) && (
                    <div className="upload-thumb-grid">
                      {images.map((img) => {
                        const src = img.image_url?.startsWith("/api/") ? `${config.apiUrl}${img.image_url.slice(4)}` : img.image_url;
                        return (
                          <div key={img.id} className="upload-thumb" onClick={() => setLightboxUrl(src)}>
                            <img src={src} alt={img.original_filename} />
                            <button
                              className="upload-thumb-remove"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await removeListingImage(img.id);
                                setImages((prev) => prev.filter((i) => i.id !== img.id));
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        );
                      })}
                      {pendingImages.map((p, i) => (
                        <div key={`pending-${i}`} className="upload-thumb upload-thumb--pending" onClick={() => setLightboxUrl(p.localUrl)}>
                          <img src={p.localUrl} alt={p.file.name} />
                          <button
                            className="upload-thumb-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              URL.revokeObjectURL(p.localUrl);
                              setPendingImages((prev) => prev.filter((_, j) => j !== i));
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <span className="upload-thumb-pending-badge">{t("Pending")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="upload-grid">
                    {/* Product Photo upload */}
                    <div
                      className="upload-item"
                      onClick={() => !imgLoading && photoInputRef.current?.click()}
                    >
                      <div className="upload-icon-small">
                        {imgLoading
                          ? <FontAwesomeIcon icon={faSpinner} spin />
                          : <FontAwesomeIcon icon={faImage} />
                        }
                      </div>
                      <div className="upload-info">
                        <span className="upload-name">{t("Product Photo")}</span>
                        <span className="upload-status">
                          {imgLoading ? t("Uploading...") : t("Click to upload")}
                        </span>
                      </div>
                      <input
                        ref={photoInputRef}
                        type="file"
                        className="upload-input"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const localUrl = URL.createObjectURL(file);
                          setPendingImages((prev) => [...prev, { file, localUrl, label: "product" }]);
                          e.target.value = "";
                        }}
                      />
                    </div>
                    <div className="upload-item upload-item--wip">
                      <div className="upload-icon-small">
                        <FontAwesomeIcon icon={faFileAlt} />
                      </div>
                      <div className="upload-info">
                        <span className="upload-name">
                          {t("Specifications")}
                          <span className="upload-wip-badge">WIP</span>
                        </span>
                        <span className="upload-status">{t("Click to upload")}</span>
                      </div>
                    </div>
                    <div className="upload-item upload-item--wip">
                      <div className="upload-icon-small">
                        <FontAwesomeIcon icon={faShieldAlt} />
                      </div>
                      <div className="upload-info">
                        <span className="upload-name">
                          {t("Safety Data")}
                          <span className="upload-wip-badge">WIP</span>
                        </span>
                        <span className="upload-status">{t("Click to upload")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon red">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <h4 className="info-card-title">{t("Pickup Location")}</h4>
                </div>
                <div className="info-card-content">
                  <div className="field-row">
                    <label className="field-label" htmlFor="address">
                      {t("Address")}
                    </label>
                    <input
                      id="address"
                      type="text"
                      className={`field-input ${
                        formData.use_default_address ? "disabled" : ""
                      }`}
                      value={formData.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder={t("Enter address")}
                      disabled={formData.use_default_address}
                    />
                  </div>
                  <div className="field-row">
                    <label className="field-label" htmlFor="city">
                      {t("City")}
                    </label>
                    <input
                      id="city"
                      type="text"
                      className={`field-input ${
                        formData.use_default_address ? "disabled" : ""
                      }`}
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      placeholder={t("Enter city")}
                      disabled={formData.use_default_address}
                    />
                  </div>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={formData.use_default_address}
                      onChange={(e) =>
                        handleCheckboxChange(
                          "use_default_address",
                          e.target.checked
                        )
                      }
                    />
                    <span className="toggle-label">
                      {t("Use default address")}
                    </span>
                  </label>
                </div>
              </div>

              {/* Contact Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon purple">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <h4 className="info-card-title">{t("Contact Info")}</h4>
                </div>
                <div className="info-card-content">
                  <div className="field-row">
                    <label className="field-label" htmlFor="contact_email">
                      {t("Email")}
                    </label>
                    <input
                      id="contact_email"
                      type="email"
                      className={`field-input ${
                        !formData.share_contact ? "disabled" : ""
                      }`}
                      value={formData.contact_email}
                      onChange={(e) =>
                        handleChange("contact_email", e.target.value)
                      }
                      placeholder={t("Enter email")}
                      disabled={!formData.share_contact}
                    />
                  </div>
                  <div className="field-row">
                    <label className="field-label" htmlFor="contact_phone">
                      {t("Phone")}
                    </label>
                    <input
                      id="contact_phone"
                      type="tel"
                      className={`field-input ${
                        !formData.share_contact ? "disabled" : ""
                      }`}
                      value={formData.contact_phone}
                      onChange={(e) =>
                        handleChange("contact_phone", e.target.value)
                      }
                      placeholder={t("Enter phone")}
                      disabled={!formData.share_contact}
                    />
                  </div>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={formData.share_contact}
                      onChange={(e) =>
                        handleCheckboxChange("share_contact", e.target.checked)
                      }
                    />
                    <span className="toggle-label">
                      {t("Share contact on listing")}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <UnsavedChangesModal
          open={showUnsavedModal}
          onClose={handleCancelClose}
          onDiscard={handleDiscardAndClose}
          darkMode={darkMode}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        {lightboxUrl && createPortal(
          <div className="upload-lightbox" onClick={() => setLightboxUrl(null)}>
            <img src={lightboxUrl} alt="preview" onClick={e => e.stopPropagation()} onError={e => { e.target.style.display = "none"; }} />
            <button className="upload-lightbox-close" onClick={() => setLightboxUrl(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>,
          document.body
        )}
      </>
    );
  }
);

DetailedViewPanel.displayName = "DetailedViewPanel";

DetailedViewPanel.propTypes = {
  row: PropTypes.object,
  darkMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  listingsData: PropTypes.array,
  saveVersion: PropTypes.number.isRequired,
  onUnsavedChangesChange: PropTypes.func,
};

export default DetailedViewPanel;
